import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Sparkles, Zap, Shield, Heart, Star, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAGUES = [
  { name: 'Premier League', flag: 'EN', teams: ['Arsenal', 'Manchester City', 'Liverpool', 'Chelsea', 'Manchester United', 'Tottenham', 'Newcastle', 'Aston Villa'] },
  { name: 'La Liga', flag: 'ES', teams: ['Real Madrid', 'Barcelona', 'Atletico Madrid', 'Real Sociedad', 'Real Betis', 'Villarreal', 'Sevilla', 'Athletic Bilbao'] },
  { name: 'Serie A', flag: 'IT', teams: ['Inter Milan', 'AC Milan', 'Napoli', 'Juventus', 'AS Roma', 'Lazio', 'Atalanta', 'Fiorentina'] },
  { name: 'Bundesliga', flag: 'DE', teams: ['Bayern Munich', 'Borussia Dortmund', 'RB Leipzig', 'Bayer Leverkusen', 'Wolfsburg', 'Frankfurt', 'Stuttgart', 'Freiburg'] },
  { name: 'Ligue 1', flag: 'FR', teams: ['PSG', 'Marseille', 'Monaco', 'Lyon', 'Lille', 'Nice', 'Lens', 'Rennes'] },
  { name: 'MLS', flag: 'US', teams: ['Inter Miami', 'LA Galaxy', 'LAFC', 'Atlanta United', 'Seattle Sounders', 'Portland Timbers', 'NYC FC', 'Austin FC'] },
  { name: 'World Cup Favorites', flag: 'WC', teams: ['Brazil', 'Argentina', 'France', 'Germany', 'England', 'Spain', 'Portugal', 'Netherlands'] },
];

const ROOT_BLESSINGS = [
  { name: 'Match Day Grounding', price: 7.77, icon: Shield, color: '#E74C3C',
    desc: 'A Root Chakra energy charge sent to your team before kickoff. Red Jasper activation for stability under pressure.' },
  { name: 'Season-Long Root Shield', price: 33.33, icon: Zap, color: '#FF6B9D',
    desc: 'Continuous grounding energy for the entire season. Bloodstone + Black Tourmaline grid activated weekly for your team.' },
  { name: 'Championship Manifestation', price: 77.77, icon: Star, color: '#c9a96e',
    desc: 'The ultimate Root Chakra ceremony. A dedicated crystal grid with Garnet, Red Jasper, and Smoky Quartz channeling victory energy all season.' },
];

const FifaRootPage = () => {
  usePageTitle('Root for Your Team - Crystal Energy Blessing');

  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedBlessing, setSelectedBlessing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '' });
  const [boostedEnergy, setBoostedEnergy] = useState(0);

  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
    setBoostedEnergy(0);
  };

  const boostEnergy = () => {
    if (boostedEnergy >= 100) return;
    setBoostedEnergy(prev => Math.min(prev + Math.floor(Math.random() * 15) + 5, 100));
  };

  const handleFormChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBlessing || !selectedTeam) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/root-blessing/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData, team: selectedTeam, league: selectedLeague?.name,
          blessing: selectedBlessing.name, amount: selectedBlessing.price,
        }),
      });
      if (res.ok) { setSubmitted(true); toast.success('Root energy blessing activated!'); }
    } catch (err) { toast.error('Something went wrong.'); }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-20 px-6 bg-gradient-to-b from-white to-[#faf8f5] overflow-hidden">
        <div className="absolute top-10 left-1/4 w-80 h-80 bg-[#E74C3C]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-[#FFB800]/10 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E74C3C]/10 border border-[#E74C3C]/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#E74C3C] animate-pulse" />
            <span className="text-xs text-[#E74C3C] tracking-[3px] uppercase font-bold">Root Chakra Energy &middot; Football Edition</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1e1919] mb-4" data-testid="fifa-root-title">
            Root <span className="text-[#E74C3C]">for Your Team</span>
          </h1>
          <p className="text-[#61525a] max-w-2xl mx-auto text-base leading-relaxed mb-6">
            Channel the primal power of the Root Chakra — the energy center of stability, strength, and survival —
            directly into your team's performance. Select your squad, charge their energy, and activate a crystal blessing.
          </p>
          <div className="inline-flex items-center gap-4 text-sm text-[#7A7A9A]">
            <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-[#E74C3C]" /> Muladhara Energy</span>
            <span className="w-1 h-1 rounded-full bg-[#c9a96e]" />
            <span className="flex items-center gap-1"><Zap className="w-4 h-4 text-[#FFB800]" /> 396 Hz Grounding</span>
            <span className="w-1 h-1 rounded-full bg-[#c9a96e]" />
            <span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#c9a96e]" /> Crystal Charged</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* League Selection */}
        <div className="mb-12" data-testid="league-selection">
          <h2 className="font-serif text-2xl text-[#1e1919] text-center mb-6">Choose Your League</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {LEAGUES.map(league => (
              <button
                key={league.name}
                onClick={() => { setSelectedLeague(league); setSelectedTeam(null); setBoostedEnergy(0); }}
                className={`px-5 py-3 rounded-xl text-sm font-semibold transition-all border-2 ${
                  selectedLeague?.name === league.name
                    ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C] shadow-lg'
                    : 'border-transparent bg-white text-[#4A4A6A] hover:border-[#e8e4de] hover:shadow-md'
                }`}
                data-testid={`league-${league.flag.toLowerCase()}`}
              >
                {league.flag} {league.name}
              </button>
            ))}
          </div>
        </div>

        {/* Team Selection */}
        {selectedLeague && (
          <div className="mb-12" data-testid="team-selection">
            <h2 className="font-serif text-2xl text-[#1e1919] text-center mb-6">Select Your Team</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {selectedLeague.teams.map(team => (
                <button
                  key={team}
                  onClick={() => handleTeamSelect(team)}
                  className={`p-4 rounded-xl text-center transition-all border-2 ${
                    selectedTeam === team
                      ? 'border-[#E74C3C] bg-[#E74C3C]/5 shadow-lg scale-105'
                      : 'border-transparent bg-white hover:border-[#e8e4de] hover:shadow-sm'
                  }`}
                  data-testid={`team-${team.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <p className="font-bold text-sm text-[#1e1919]">{team}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Energy Boost Interactive */}
        {selectedTeam && (
          <div className="mb-12 bg-white rounded-3xl p-8 shadow-xl border border-[#e8e4de]" data-testid="energy-boost">
            <div className="text-center mb-6">
              <h3 className="font-serif text-2xl text-[#1e1919] mb-2">
                Charge <span className="text-[#E74C3C]">{selectedTeam}'s</span> Root Energy
              </h3>
              <p className="text-[#7A7A9A] text-sm">Tap the crystal to channel grounding energy. Reach 100% for maximum effect!</p>
            </div>

            {/* Energy Bar */}
            <div className="max-w-md mx-auto mb-6">
              <div className="flex justify-between text-xs text-[#7A7A9A] mb-2">
                <span>Root Energy Level</span>
                <span className="font-bold text-[#E74C3C]">{boostedEnergy}%</span>
              </div>
              <div className="h-4 bg-[#faf8f5] rounded-full overflow-hidden border border-[#e8e4de]">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-[#E74C3C] via-[#FF6B9D] to-[#FFB800]"
                  style={{ width: `${boostedEnergy}%` }}
                />
              </div>
            </div>

            {/* Tap Crystal */}
            <div className="flex justify-center mb-6">
              <button
                onClick={boostEnergy}
                disabled={boostedEnergy >= 100}
                className={`w-24 h-24 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                  boostedEnergy >= 100
                    ? 'bg-gradient-to-br from-[#00C853] to-[#00E676] shadow-xl shadow-[#00C853]/30'
                    : 'bg-gradient-to-br from-[#E74C3C] to-[#FF6B9D] shadow-xl shadow-[#E74C3C]/30 hover:scale-105 cursor-pointer'
                }`}
                data-testid="boost-crystal-btn"
              >
                {boostedEnergy >= 100 ? (
                  <CheckCircle className="w-10 h-10 text-white" />
                ) : (
                  <Zap className="w-10 h-10 text-white" />
                )}
              </button>
            </div>
            <p className="text-center text-xs text-[#7A7A9A]">
              {boostedEnergy >= 100 ? 'Maximum energy reached! Choose a blessing below.' : 'Tap the crystal to boost energy!'}
            </p>
          </div>
        )}

        {/* Blessing Packages */}
        {selectedTeam && (
          <div className="mb-12" data-testid="blessing-packages">
            <h2 className="font-serif text-2xl text-[#1e1919] text-center mb-6">Root Chakra Blessings</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {ROOT_BLESSINGS.map((blessing) => {
                const Icon = blessing.icon;
                const selected = selectedBlessing?.name === blessing.name;
                return (
                  <button
                    key={blessing.name}
                    onClick={() => setSelectedBlessing(blessing)}
                    className={`p-6 rounded-2xl text-left transition-all border-2 ${
                      selected
                        ? 'border-[#c9a96e] bg-[#c9a96e]/5 shadow-xl scale-[1.02]'
                        : 'border-transparent bg-white hover:border-[#e8e4de] hover:shadow-md'
                    }`}
                    data-testid={`blessing-${blessing.price}`}
                  >
                    <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center" style={{ backgroundColor: `${blessing.color}15` }}>
                      <Icon className="w-6 h-6" style={{ color: blessing.color }} />
                    </div>
                    <h3 className="font-serif text-lg text-[#1e1919] mb-1">{blessing.name}</h3>
                    <p className="text-sm text-[#61525a] mb-3">{blessing.desc}</p>
                    <p className="font-serif text-2xl text-[#c9a96e]">${blessing.price}</p>
                    {selected && <span className="inline-block mt-2 text-xs text-[#c9a96e] font-bold uppercase">Selected</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Purchase Form */}
        {selectedBlessing && !submitted && (
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#e8e4de] max-w-lg mx-auto" data-testid="blessing-form">
            <div className="text-center mb-6">
              <h3 className="font-serif text-xl text-[#1e1919] mb-1">Activate Blessing for {selectedTeam}</h3>
              <p className="text-sm text-[#7A7A9A]">{selectedBlessing.name} — ${selectedBlessing.price}</p>
            </div>
            {!showForm ? (
              <button onClick={() => setShowForm(true)}
                className="w-full py-4 bg-gradient-to-r from-[#E74C3C] to-[#FF6B9D] text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all"
                data-testid="activate-blessing-btn"
              >
                Activate Root Blessing — ${selectedBlessing.price}
              </button>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input type="text" name="fullName" placeholder="Full Name *" value={formData.fullName} onChange={handleFormChange} required
                  className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#E74C3C] focus:border-transparent outline-none text-sm" data-testid="bless-fullname" />
                <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleFormChange} required
                  className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#E74C3C] focus:border-transparent outline-none text-sm" data-testid="bless-email" />
                <input type="tel" name="phone" placeholder="Phone (optional)" value={formData.phone} onChange={handleFormChange}
                  className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#E74C3C] focus:border-transparent outline-none text-sm" data-testid="bless-phone" />
                <p className="text-[10px] text-[#7A7A9A] text-center">Payment processing via Zoho Payments (coming soon).</p>
                <button type="submit" disabled={submitting}
                  className="w-full py-4 bg-gradient-to-r from-[#E74C3C] to-[#FF6B9D] text-white rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="bless-submit"
                >
                  {submitting ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Send className="w-4 h-4" /> Confirm — ${selectedBlessing.price}</>}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Success */}
        {submitted && (
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#00C853]/30 max-w-lg mx-auto text-center" data-testid="blessing-success">
            <CheckCircle className="w-12 h-12 text-[#00C853] mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Root Energy Activated!</h3>
            <p className="text-[#61525a] text-sm">
              {selectedBlessing.name} has been activated for <strong>{selectedTeam}</strong>.
              The crystal grid is being charged. You'll receive confirmation via email.
            </p>
            <p className="mt-4 text-xs text-[#c9a96e]">Gemstone Minerals&reg; &middot; Kholey.com&reg;</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FifaRootPage;
